/**
 * Audit Tracking Hook
 * Tracks important user interactions and sends them to the backend for audit logging.
 *
 * Tracks: page navigation, deletions, data exports, bulk operations.
 * Does NOT track: button clicks, modal opens, form submits, searches, filter changes.
 * Those low-value events were removed to reduce audit log noise (~90% reduction).
 */

import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

/**
 * Helper to get readable page name from path
 */
const getPageName = (path) => {
  const pathSegments = path.split('/').filter(Boolean);

  if (pathSegments.length === 0) return 'Home';

  const pageMap = {
    admin: 'Admin Portal',
    bureau: 'Bureau Portal',
    client: 'Client Portal',
    'super-admin': 'Super Admin Portal',
    sales: 'Sales Portal',
    support: 'Support Portal',
    tiplock: 'TipLock Portal',
    users: 'User Management',
    dashboard: 'Dashboard',
    fleet: 'Fleet Management',
    alerts: 'Alerts',
    events: 'Events',
    monitoring: 'Monitoring',
    devices: 'Devices',
    vehicles: 'Vehicles',
    settings: 'Settings',
    profile: 'Profile',
    reports: 'Reports',
    analytics: 'Analytics',
  };

  const lastSegment = pathSegments[pathSegments.length - 1];
  const secondLastSegment = pathSegments[pathSegments.length - 2];

  if (pageMap[lastSegment]) return pageMap[lastSegment];

  if (secondLastSegment && pageMap[secondLastSegment]) {
    return `${pageMap[secondLastSegment]} - ${lastSegment}`;
  }

  return lastSegment
    .split('-')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

/**
 * Helper to get portal from path
 */
const getPortalFromPath = (path) => {
  if (path.includes('/admin')) return 'admin';
  if (path.includes('/bureau')) return 'bureau';
  if (path.includes('/client')) return 'client';
  if (path.includes('/super-admin')) return 'super-admin';
  if (path.includes('/sales')) return 'sales';
  if (path.includes('/support')) return 'support';
  if (path.includes('/tiplock')) return 'tiplock';
  return 'unknown';
};

export const useAuditTracking = () => {
  const location = useLocation();
  const previousPath = useRef(location.pathname);
  const user = useSelector((state) => state.auth?.user);
  const token = useSelector((state) => state.auth?.accessToken);

  // Track page navigation
  useEffect(() => {
    if (previousPath.current !== location.pathname) {
      trackPageNavigation(previousPath.current, location.pathname);
      previousPath.current = location.pathname;
    }
  }, [location]);

  /**
   * Send audit event to backend
   */
  const sendAuditEvent = async (eventData) => {
    try {
      await axios.post(`${API_BASE_URL}/api/v1/rbac/audit-logs/frontend-event/`, eventData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
    } catch (error) {
      // Silently fail to not disrupt user experience
      console.debug('Audit tracking failed:', error.message);
    }
  };

  /**
   * Track page navigation events
   */
  const trackPageNavigation = async (fromPath, toPath) => {
    if (!user || !token) return;

    try {
      await sendAuditEvent({
        event_type: 'page_view',
        severity: 'low',
        description: `Navigated from ${getPageName(fromPath)} to ${getPageName(toPath)}`,
        module: 'frontend',
        action: 'navigation',
        metadata: {
          from_path: fromPath,
          to_path: toPath,
          page_name: getPageName(toPath),
          portal: getPortalFromPath(toPath),
        },
      });
    } catch (error) {
      console.debug('Failed to track page navigation:', error.message);
    }
  };

  /**
   * Track deletion events (high-value action)
   */
  const trackDeletion = async (entityType, entityName, success = true, context = {}) => {
    if (!user || !token) return;

    try {
      await sendAuditEvent({
        event_type: 'api_delete',
        severity: 'high',
        description: `Deleted ${entityType}: ${entityName} ${success ? 'successfully' : '(failed)'}`,
        module: 'frontend',
        action: 'delete',
        metadata: {
          entity_type: entityType,
          entity_name: entityName,
          success,
          page: getPageName(location.pathname),
          ...context,
        },
      });
    } catch (error) {
      console.debug('Failed to track deletion:', error.message);
    }
  };

  /**
   * Track data exports (high-value action)
   */
  const trackDataExport = async (exportType, dataType, context = {}) => {
    if (!user || !token) return;

    try {
      await sendAuditEvent({
        event_type: 'api_export',
        severity: 'medium',
        description: `Exported ${dataType} as ${exportType}`,
        module: 'frontend',
        action: 'data_export',
        metadata: {
          export_type: exportType,
          data_type: dataType,
          page: getPageName(location.pathname),
          ...context,
        },
      });
    } catch (error) {
      console.debug('Failed to track data export:', error.message);
    }
  };

  /**
   * Track bulk operations (high-value action)
   */
  const trackBulkOperation = async (operationType, itemCount, entityType, context = {}) => {
    if (!user || !token) return;

    try {
      await sendAuditEvent({
        event_type: 'api_update',
        severity: 'medium',
        description: `Bulk ${operationType}: ${itemCount} ${entityType}`,
        module: 'frontend',
        action: 'bulk_operation',
        metadata: {
          operation_type: operationType,
          item_count: itemCount,
          entity_type: entityType,
          page: getPageName(location.pathname),
          ...context,
        },
      });
    } catch (error) {
      console.debug('Failed to track bulk operation:', error.message);
    }
  };

  /**
   * Track caught errors with context (for components to manually report)
   */
  const trackError = async (error, context = {}) => {
    if (!user || !token) return;

    try {
      const errorMessage = error?.message || String(error);
      const stack = error?.stack ? error.stack.slice(0, 800) : '';

      await sendAuditEvent({
        event_type: 'system_error',
        severity: 'high',
        description: `Frontend error: ${errorMessage.slice(0, 200)}`,
        module: 'frontend',
        action: 'js_error',
        metadata: {
          error_message: errorMessage,
          stack_trace: stack,
          page: getPageName(location.pathname),
          portal: getPortalFromPath(location.pathname),
          url: window.location.href,
          ...context,
        },
      });
    } catch (err) {
      console.debug('Failed to track error:', err.message);
    }
  };

  return {
    trackPageNavigation,
    trackDeletion,
    trackDataExport,
    trackBulkOperation,
    trackError,
  };
};

// ── Global Error Listeners ────────────────────────────────────────────────
const GLOBAL_ERROR_THROTTLE_MAX = 5; // max errors per minute
let globalErrorCount = 0;
let globalErrorResetTimer = null;

const resetGlobalErrorCount = () => {
  globalErrorCount = 0;
  globalErrorResetTimer = null;
};

const canLogGlobalError = () => {
  if (globalErrorCount >= GLOBAL_ERROR_THROTTLE_MAX) return false;
  globalErrorCount++;
  if (!globalErrorResetTimer) {
    globalErrorResetTimer = setTimeout(resetGlobalErrorCount, 60000);
  }
  return true;
};

const sendGlobalErrorEvent = (payload) => {
  try {
    // Read token from localStorage/sessionStorage as fallback since this runs outside React
    const persistedAuth = localStorage.getItem('persist:auth') || sessionStorage.getItem('persist:auth');
    let token = null;
    if (persistedAuth) {
      try {
        const parsed = JSON.parse(persistedAuth);
        token = typeof parsed.token === 'string'
          ? parsed.token.replace(/^"|"$/g, '')
          : parsed.token;
      } catch {
        // ignore parse error
      }
    }
    if (!token) return;

    const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

    fetch(`${API_BASE}/api/v1/rbac/audit-logs/frontend-event/`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    }).catch(() => {});
  } catch {
    // Silently ignore
  }
};

/**
 * Global tracking initialization.
 * Registers error-only listeners (uncaught JS errors + unhandled promise rejections).
 * Throttled to max 5 errors per minute to prevent flood.
 */
export const initializeGlobalTracking = () => {
  // Uncaught JS errors
  window.addEventListener('error', (event) => {
    if (!canLogGlobalError()) return;

    sendGlobalErrorEvent({
      event_type: 'system_error',
      severity: 'high',
      description: `Uncaught error: ${(event.message || 'Unknown').slice(0, 200)}`,
      module: 'frontend',
      action: 'js_error',
      metadata: {
        error_message: event.message,
        source_file: event.filename,
        line: event.lineno,
        column: event.colno,
        stack_trace: event.error?.stack?.slice(0, 800) || '',
        page: window.location.pathname,
        portal: getPortalFromPath(window.location.pathname),
        url: window.location.href,
      },
    });
  });

  // Unhandled promise rejections
  window.addEventListener('unhandledrejection', (event) => {
    if (!canLogGlobalError()) return;

    const reason = event.reason;
    const message = reason?.message || String(reason || 'Unknown rejection');
    const stack = reason?.stack?.slice(0, 800) || '';

    sendGlobalErrorEvent({
      event_type: 'system_error',
      severity: 'high',
      description: `Unhandled rejection: ${message.slice(0, 200)}`,
      module: 'frontend',
      action: 'js_error',
      metadata: {
        error_message: message,
        stack_trace: stack,
        page: window.location.pathname,
        portal: getPortalFromPath(window.location.pathname),
        url: window.location.href,
      },
    });
  });
};
